import React, { useState } from 'react';
import { Accordion, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Check, Minus } from 'lucide-react';

const FeaturesComparison = () => {
  const { t } = useTranslation();
  const [activeKey, setActiveKey] = useState(null);

  const categories = {
    [t("CATEGORY_CORE")]: [
      t("FEATURE_PRODUCT_DB"),
      t("FEATURE_SALES_DOCTOR"),
      t("FEATURE_AUTO_ADD_TO_WAREHOUSE"),
      t("FEATURE_TAX_INTEGRATION"),
      t("FEATURE_PAYMENT_SYSTEMS"),
      t("FEATURE_CURRENCY"),
      t("FEATURE_NOMENCLATURE"),
      t("FEATURE_RETURN_TO_SUPPLIER"),
      t("FEATURE_WRITE_OFF"),
      t("FEATURE_INVENTORY"),
      t("FEATURE_TRANSFER_PRODUCTS"),
      t("FEATURE_GROUPING"),
      t("FEATURE_SCALES"),
      t("FEATURE_BUNDLES"),
      t("FEATURE_PROMOTIONS"),
      t("FEATURE_LOW_STOCK_ALERT")
    ],
    [t("CATEGORY_REPORTING")]: [
      t("FEATURE_STOCK_REPORT"),
      t("FEATURE_RECEIVED_PRODUCTS_REPORT"),
      t("FEATURE_SALES_REPORT"),
      t("FEATURE_EXPENSE_REPORT"),
      t("FEATURE_EXPIRY_REPORT"),
      t("FEATURE_RETURNED_PRODUCTS_REPORT"),
      t("FEATURE_GROSS_PROFIT_REPORT"),
      t("FEATURE_CATEGORY_MANAGEMENT"),
      t("FEATURE_SUPPLIER_REPORT"),
      t("FEATURE_CUSTOMER_REPORT"),
      t("FEATURE_WRITE_OFF_REPORT"),
      t("FEATURE_CASHIER_REPORT"),
      t("FEATURE_BONUS_REPORT"),
      t("FEATURE_FINAL_REPORT")
    ],
    [t("CATEGORY_ANALYTICS")]: [
      t("FEATURE_RECOMMENDED_PRODUCTS"),
      t("FEATURE_FROZEN_FUNDS"),
      t("FEATURE_OOS_ANALYSIS"),
      t("FEATURE_ABC_ANALYSIS"),
      t("FEATURE_MARGIN_CATEGORY"),
      t("FEATURE_PRODUCT_MOVEMENT")
    ],
    [t("CATEGORY_CUSTOMER_COMM")]: [
      t("FEATURE_TELEGRAM_DEBTOR"),
      t("FEATURE_TELEGRAM_NOTIFICATIONS"),
      t("FEATURE_TELEGRAM_STOCK"),
      t("FEATURE_TELEGRAM_BONUS")
    ],
    [t("CATEGORY_DEVICES")]: [
      t("FEATURE_ADMIN_WEB"),
      t("FEATURE_DESKTOP_POS"),
      t("FEATURE_MOBILE_POS")
    ]
  };

  const plans = ["Start", "Standart", "Premium"];

  const data = {
    [t("FEATURE_PRODUCT_DB")]: [true, true, true],
    [t("FEATURE_SALES_DOCTOR")]: [true, true, true],
    [t("FEATURE_AUTO_ADD_TO_WAREHOUSE")]: [true, true, true],
    [t("FEATURE_TAX_INTEGRATION")]: [true, true, true],
    [t("FEATURE_PAYMENT_SYSTEMS")]: [true, true, true],
    [t("FEATURE_CURRENCY")]: [false, true, true],
    [t("FEATURE_NOMENCLATURE")]: [true, true, true],
    [t("FEATURE_RETURN_TO_SUPPLIER")]: [true, true, true],
    [t("FEATURE_WRITE_OFF")]: [true, true, true],
    [t("FEATURE_INVENTORY")]: [true, true, true],
    [t("FEATURE_TRANSFER_PRODUCTS")]: [false, true, true],
    [t("FEATURE_GROUPING")]: [true, true, true],
    [t("FEATURE_SCALES")]: [true, true, true],
    [t("FEATURE_BUNDLES")]: [false, true, true],
    [t("FEATURE_PROMOTIONS")]: [false, true, true],
    [t("FEATURE_LOW_STOCK_ALERT")]: [false, true, true],

    [t("FEATURE_STOCK_REPORT")]: [true, true, true],
    [t("FEATURE_RECEIVED_PRODUCTS_REPORT")]: [true, true, true],
    [t("FEATURE_SALES_REPORT")]: [true, true, true],
    [t("FEATURE_EXPENSE_REPORT")]: [false, true, true],
    [t("FEATURE_EXPIRY_REPORT")]: [true, true, true],
    [t("FEATURE_RETURNED_PRODUCTS_REPORT")]: [true, true, true],
    [t("FEATURE_GROSS_PROFIT_REPORT")]: [true, true, true],
    [t("FEATURE_CATEGORY_MANAGEMENT")]: [true, true, true],
    [t("FEATURE_SUPPLIER_REPORT")]: [true, true, true],
    [t("FEATURE_CUSTOMER_REPORT")]: [true, true, true],
    [t("FEATURE_WRITE_OFF_REPORT")]: [true, true, true],
    [t("FEATURE_CASHIER_REPORT")]: [false, true, true],
    [t("FEATURE_BONUS_REPORT")]: [false, false, true],
    [t("FEATURE_FINAL_REPORT")]: [true, true, true],

    [t("FEATURE_RECOMMENDED_PRODUCTS")]: [false, true, true],
    [t("FEATURE_FROZEN_FUNDS")]: [false, true, true],
    [t("FEATURE_OOS_ANALYSIS")]: [false, false, true],
    [t("FEATURE_ABC_ANALYSIS")]: [false, false, true],
    [t("FEATURE_MARGIN_CATEGORY")]: [false, false, true],
    [t("FEATURE_PRODUCT_MOVEMENT")]: [false, false, true],

    [t("FEATURE_TELEGRAM_DEBTOR")]: [true, true, true],
    [t("FEATURE_TELEGRAM_NOTIFICATIONS")]: [false, false, true],
    [t("FEATURE_TELEGRAM_STOCK")]: [true, true, true],
    [t("FEATURE_TELEGRAM_BONUS")]: [false, false, true],

    [t("FEATURE_ADMIN_WEB")]: [true, true, true],
    [t("FEATURE_DESKTOP_POS")]: [true, true, true],
    [t("FEATURE_MOBILE_POS")]: [true, true, true]
  };

  return (
    <Accordion activeKey={activeKey} onSelect={setActiveKey} className="mt-4">
      {Object.entries(categories).map(([category, features], idx) => (
        <Accordion.Item eventKey={idx.toString()} key={category}>
          <Accordion.Header>{category}</Accordion.Header>
          <Accordion.Body>
            <Table responsive bordered hover>
              <thead>
                <tr>
                  <th>{t("TABLE_FEATURES")}</th>
                  {plans.map((plan) => (
                    <th key={plan} className="text-center">{plan}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {features.map((feature) => (
                  <tr key={feature}>
                    <td>{feature}</td>
                    {data[feature].map((available, i) => (
                      <td key={i} className="text-center">
                        {available ? <Check color="green" size={18} /> : <Minus color="gray" size={18} />}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </Accordion.Body>
        </Accordion.Item>
      ))}
    </Accordion>
  );
};

export default FeaturesComparison;
